import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useSurahList } from '@/hooks/useSurah';
import { revelationLabel } from '@/lib/utils/format';
import { cn } from '@/lib/utils/cn';
import {
  IconArrowRight,
  IconAward,
  IconBookOpen,
  IconCheck,
  IconChevronRight,
  IconFocus,
  IconLightbulb,
  IconListChoose,
  IconPlus,
  IconRefresh,
  IconRepeat,
  IconSearch,
  IconSparkle,
} from '@/components/home/icons';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { MobileNav } from '@/components/layout/MobileNav';

/**
 * Home — "Bring the Quran Into Your Everyday".
 *
 * A standalone, full-bleed dark landing page. The section order is adapted
 * verbatim from the Stitch design (nav → hero → strip → ask bar → surah
 * grid → tools → start-here → CTA → FAQ → footer); the generic agency
 * placeholder content it shipped with is replaced throughout with real
 * Quran-product content and live routes.
 *
 * It deliberately does not use AppShell — it carries its own dark chrome.
 */
export function Home() {
  useDocumentTitle(null);

  return (
    <div className="min-h-dvh bg-ink-50 font-sans text-ink-900 antialiased selection:bg-beam/25 selection:text-beam-pale dark:bg-night-900 dark:text-night-50">
      <Header />
      {/* pb-24 reserves space for the MobileNav (~68px + safe-area) on
          mobile and tablet; nav is hidden ≥lg so padding drops to 0. */}
      <main id="main" className="pb-24 lg:pb-0">
        <Hero />
        <ScriptureStrip />
        <EarnCoins />
        <AskBar />
        <SurahGrid />
        <StudyTools />
        <OccasionSurahs />
        <HomeCta />
        <HomeFaq />
      </main>
      <Footer />
      <MobileNav />
    </div>
  );
}

/* ================================================================== *
 * Shared bits
 * ================================================================== */

const SHELL = 'mx-auto w-full max-w-7xl px-5 sm:px-8';

/** Card surface — the design's "neon-border / glass-panel" pattern. */
const PANEL =
  'rounded-2xl border border-night-500/40 bg-night-700/60 backdrop-blur-sm';

function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <span className="text-xs font-semibold uppercase tracking-[0.2em] text-beam">
      {children}
    </span>
  );
}

function SectionHead({
  eyebrow,
  title,
  lead,
}: {
  eyebrow: string;
  title: ReactNode;
  lead?: string;
}) {
  return (
    <div className="mx-auto max-w-2xl text-center">
      <Eyebrow>{eyebrow}</Eyebrow>
      {/* Headline: deep navy-green in light, off-white in dark.
          Sections that intentionally STAY dark in light mode (e.g.
          SurahGrid) wraps its content in `dark` so these
          dark: variants activate even when the root theme is light. */}
      <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-[#183B32] sm:text-4xl dark:text-night-50">
        {title}
      </h2>
      {lead && (
        <p className="mt-4 leading-relaxed text-ink-700 dark:text-night-200">
          {lead}
        </p>
      )}
    </div>
  );
}

type ButtonLinkProps = {
  to: string;
  variant?: 'solid' | 'outline';
  children: ReactNode;
  className?: string;
};

/** Pill button used for primary/secondary CTAs across the page. */
function ButtonLink({
  to,
  variant = 'solid',
  children,
  className,
}: ButtonLinkProps) {
  return (
    <Link
      to={to}
      className={cn(
        'inline-flex h-12 items-center justify-center gap-2 rounded-xl px-7',
        'text-sm font-semibold transition-all duration-200 ease-smooth',
        variant === 'solid'
          ? 'bg-beam text-beam-ink hover:shadow-[0_0_24px_-4px_rgba(0,229,255,0.55)]'
          : // Outline button — uses deep-teal accent in light for clearer
            // contrast against ivory backgrounds; keeps beam in dark.
            'border border-accent/40 text-accent hover:border-accent hover:bg-accent/5 dark:border-beam/40 dark:text-beam-pale dark:hover:border-beam dark:hover:bg-beam/10',
        className,
      )}
    >
      {children}
    </Link>
  );
}

/* ================================================================== *
 * QariServicesGlowButton
 *
 * Hero's secondary CTA with a continuously-rotating gradient border.
 *
 * Technique: an absolutely-positioned conic-gradient layer rotates
 * behind the button; the button's solid background masks the center,
 * leaving only the rotating ring visible through a 1.5px gap. The
 * gradient sweeps from warm gold → beam-cyan → emerald → back, so the
 * "marching light" reads as on-brand rather than a generic neon trail.
 *
 * Honors prefers-reduced-motion: the rotation pauses, leaving a static
 * gradient ring — still distinctive, no nausea risk.
 * ================================================================== */

function QariServicesGlowButton() {
  return (
    <Link
      to="/find-qari"
      className={cn(
        'group relative inline-flex h-12 items-center justify-center rounded-xl p-[1.5px]',
        'overflow-hidden isolate',
        'qari-glow-wrap',
      )}
      aria-label="Qari Services"
    >
      {/* Rotating gradient ring — sits BEHIND the inner content via
          z-index. The conic gradient is wider than the wrapper and
          spins via CSS keyframes. */}
      <span
        aria-hidden="true"
        className="qari-glow-ring pointer-events-none absolute inset-[-50%] -z-10"
      />
      {/* Inner button face — solid background masks the center of the
          rotating ring, leaving only the ~1.5px border visible. */}
      <span
        className={cn(
          'relative z-10 inline-flex h-full w-full items-center justify-center gap-2 rounded-[10px]',
          'bg-ink-50 dark:bg-night-900',
          'px-7 text-sm font-semibold text-accent dark:text-beam-pale',
          'transition-colors duration-200',
          'group-hover:bg-accent/5 dark:group-hover:bg-beam/10',
        )}
      >
        Qari Services
      </span>

      {/* Scoped styles — keyframes + the conic gradient definition.
          Inline so we don't add a new global CSS file just for one
          button. */}
      <style>{`
        @keyframes qari-glow-spin {
          to { transform: rotate(360deg); }
        }
        .qari-glow-wrap .qari-glow-ring {
          background: conic-gradient(
            from 0deg,
            #D4AF37 0deg,
            #00E5FF 90deg,
            #4DA088 180deg,
            #D4AF37 270deg,
            #00E5FF 360deg
          );
          animation: qari-glow-spin 4s linear infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .qari-glow-wrap .qari-glow-ring { animation: none; }
        }
      `}</style>
    </Link>
  );
}

/* ================================================================== *
 * Hero
 * ================================================================== */

function Hero() {
  return (
    <section className="relative overflow-hidden bg-[#F7F3EA] dark:bg-transparent">
      {/* Atmospheric glow orbs — softened in light, hidden on small screens
          so they don't crowd narrow viewports. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-40 -top-48 hidden h-[34rem] w-[34rem] rounded-full bg-beam/8 blur-[130px] sm:block dark:bg-beam/20"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-56 -right-40 hidden h-[34rem] w-[34rem] rounded-full bg-beam-soft/8 blur-[130px] sm:block dark:bg-beam-soft/15"
      />

      <div
        className={cn(
          SHELL,
          'relative grid grid-cols-1 items-center gap-12 py-14 lg:grid-cols-2 lg:gap-10 lg:py-24',
        )}
      >
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-accent/25 bg-accent/5 px-3.5 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-accent dark:border-beam/25 dark:bg-beam/5 dark:text-beam">
            <IconSparkle className="h-3.5 w-3.5" />
            AI-assisted Quran study
          </span>

          <h1 className="mt-6 font-display text-display-hero font-extrabold text-[#183B32] dark:text-night-50">
            Bring the Quran Into Your{' '}
            <span className="text-beam">Everyday</span>
            {/* SEO subtitle inside the same H1 so Googlebot reads the
                target keyword as part of the page's primary heading
                without disrupting the brand hero visually. */}
            <span className="mt-3 block text-base font-medium tracking-normal text-ink-600 dark:text-night-300 sm:text-lg">
              AI Quran reader with Arabic, English &amp; Urdu translation
            </span>
          </h1>

          <p className="mt-6 max-w-xl text-lg leading-relaxed text-ink-700 dark:text-night-200">
            Build a daily habit with the Quran — faithful Arabic and clear
            translation, memorization tools, meaning-based search, and an AI
            companion that always shows its sources.
          </p>

          <div className="mt-9 flex flex-wrap gap-3">
            <ButtonLink to="/surah">
              Open the Quran
              <IconArrowRight className="h-4 w-4" />
            </ButtonLink>
            <QariServicesGlowButton />
          </div>

          <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3">
            {['Faithful Uthmani text', 'Every AI answer is cited'].map((t) => (
              <span
                key={t}
                className="flex items-center gap-2 text-sm text-ink-700 dark:text-night-200"
              >
                <IconCheck className="h-4 w-4 text-beam" />
                {t}
              </span>
            ))}
          </div>
        </div>

        <WorkflowOrbit />
      </div>
    </section>
  );
}

const WORKFLOW_STEPS: { label: string; Icon: typeof IconBookOpen }[] = [
  { label: 'Choose', Icon: IconListChoose },
  { label: 'Read', Icon: IconBookOpen },
  { label: 'Reflect', Icon: IconLightbulb },
  { label: 'Memorize', Icon: IconRepeat },
  { label: 'Revise', Icon: IconRefresh },
  { label: 'Master', Icon: IconAward },
];

/** The hero's circular study-loop visual. Six steps orbiting a hub. */
function WorkflowOrbit() {
  const nodes = WORKFLOW_STEPS.map((step, i) => {
    const angle = ((-90 + i * 60) * Math.PI) / 180;
    return {
      ...step,
      x: 50 + 40 * Math.cos(angle),
      y: 50 + 40 * Math.sin(angle),
    };
  });

  return (
    <div className="relative mx-auto aspect-square w-full max-w-[440px] animate-home-float">
      {/* Connective geometry. Stroke uses `currentColor` so the SVG
          adopts the parent's text color — `accent` (deep teal) in light
          mode is dark enough to read on the ivory background; `beam`
          (electric cyan) keeps the night-sky feel in dark mode. */}
      <svg
        className="absolute inset-0 h-full w-full text-accent dark:text-beam"
        viewBox="0 0 100 100"
        fill="none"
        aria-hidden="true"
      >
        <circle
          cx="50"
          cy="50"
          r="40"
          stroke="currentColor"
          strokeOpacity="0.45"
          strokeWidth="0.4"
          strokeDasharray="1.4 2.6"
          className="animate-home-orbit"
          style={{ transformOrigin: 'center' }}
        />
        {nodes.map((n) => (
          <line
            key={n.label}
            x1="50"
            y1="50"
            x2={n.x}
            y2={n.y}
            stroke="currentColor"
            strokeOpacity="0.30"
            strokeWidth="0.4"
          />
        ))}
      </svg>

      {/* Hub */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <div
          aria-hidden="true"
          className="absolute -inset-3 animate-home-pulse rounded-full bg-accent/25 blur-xl dark:bg-beam/25"
        />
        <div className="relative flex h-24 w-24 items-center justify-center rounded-full border border-beam/40 bg-night-800/90 backdrop-blur-md">
          <span
            dir="rtl"
            className="font-arabic text-3xl text-beam-pale"
            title="Iqra — &quot;Read&quot;, the first revealed word"
          >
            اقْرَأْ
          </span>
        </div>
      </div>

      {/* Orbiting steps */}
      {nodes.map(({ label, Icon, x, y }) => (
        <div
          key={label}
          className="absolute flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-2"
          style={{ left: `${x}%`, top: `${y}%` }}
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-full border border-beam/30 bg-night-700/80 text-beam backdrop-blur-sm">
            <Icon className="h-5 w-5" />
          </div>
          <span className="rounded-full border border-night-500/40 bg-night-800/90 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-night-50">
            {label}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ================================================================== *
 * Scripture strip (Stitch "Trusted By" pills → honest facts)
 * ================================================================== */

/* ================================================================== *
 * Earn Virtual Coins (Para 1–30 milestone visual placeholder)
 * ================================================================== *
 * Inserted between the scripture strip and the Ask bar. Renders the
 * heading pair plus 10 coin slots. Each slot will eventually display
 * a custom coin image (drop them under public/coins/coin-1.png … 10.png
 * — the markup picks them up automatically). Until the images are
 * uploaded each slot shows a numeric placeholder so the layout reads
 * correctly.
 *
 * The 10th slot is highlighted as the "milestone" coin per the user's
 * sketch (red circle = different from the others) — given the brand
 * palette, beam-cyan reads as the natural "achievement" tone here.
 */
/**
 * Coin animation keyframes. Two layered effects, both gated behind
 * prefers-reduced-motion:
 *
 *   1. `coin-spin` — continuous 360° Y-axis rotation on the coin face.
 *      14s per full turn at rest; hover speeds it to 1.5s for a fast
 *      flash. transform-style: preserve-3d keeps it perspective-aware.
 *
 *   2. `coin-halo-pulse` — warm amber radial-gradient ring behind the
 *      coin. Pulses opacity + scale, theme-agnostic warm gold tone
 *      that reads clearly against both ivory and night-900 backdrops.
 *
 * Amber #d4a85a chosen over cyan so the effect feels native to a
 * physical metal coin (vs the electric cyan brand accent).
 */
const COIN_KEYFRAMES = `
@media (prefers-reduced-motion: no-preference) {
  @keyframes coin-spin {
    from { transform: rotateY(0deg); }
    to   { transform: rotateY(360deg); }
  }
  @keyframes coin-halo-pulse {
    0%, 100% { opacity: 0;    transform: scale(0.92); }
    50%      { opacity: 0.55; transform: scale(1.10); }
  }
  .coin-spin {
    transform-style: preserve-3d;
    animation: coin-spin 14s linear infinite;
  }
  .coin-halo {
    background: radial-gradient(circle, rgba(212, 168, 90, 0.40) 0%, rgba(212, 168, 90, 0) 70%);
    animation: coin-halo-pulse 3.5s ease-in-out infinite;
  }
  .group:hover .coin-spin  { animation-duration: 1.5s; }
  .group:hover .coin-halo  { animation-duration: 1.5s; }
}
`;

function EarnCoins() {
  return (
    <section className="bg-[#FFFCF6] py-16 sm:py-20 dark:bg-night-950">
      <div className={SHELL}>
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-display text-3xl font-extrabold tracking-tight text-[#183B32] sm:text-4xl dark:text-night-50">
            Earn Virtual Coins
          </h2>
          <h3 className="mt-3 text-base font-medium text-ink-700 sm:text-lg dark:text-night-200">
            Collect Virtual Coins By Completing a Para
          </h3>
        </div>

        {/* Single coin — centered, with a 3D flip on hover.
            No surrounding ring; the coin's own engraved edge is the
            frame. A perpetual idle wobble (~5° each way) gives it
            life; hovering rotates it a full 360° on the Y axis.
            All animation gated behind motion-safe. */}
        <style>{COIN_KEYFRAMES}</style>
        <div className="mt-10 flex justify-center" style={{ perspective: '900px' }}>
          <div
            className={cn(
              'group relative aspect-square cursor-default select-none',
              'h-28 w-28 sm:h-32 sm:w-32',
              // Neutral drop shadow so the coin reads as a physical
              // medal on either ivory or night-900.
              'drop-shadow-[0_6px_12px_rgba(0,0,0,0.35)] dark:drop-shadow-[0_6px_18px_rgba(0,0,0,0.55)]',
            )}
            title="Al-Aziz — The All-Mighty"
            aria-label="Earned coin: Al-Aziz, The All-Mighty"
          >
            {/* Warm amber halo — pulses behind the coin. Theme-agnostic
                gold tone, sits outside the coin so it stays visible
                even while the coin face rotates. */}
            <span
              aria-hidden="true"
              className="coin-halo pointer-events-none absolute -inset-3 rounded-full"
            />

            {/* The coin itself — perpetual 3D rotation. */}
            <div className="coin-spin relative h-full w-full">
              <img
                src="/coins/coin-1.png"
                alt="Al-Aziz — The All-Mighty"
                loading="lazy"
                className="h-full w-full rounded-full object-cover"
              />
            </div>
          </div>
        </div>

        <p className="mt-6 text-center text-xs uppercase tracking-wide text-ink-500 dark:text-night-300">
          One coin per completed milestone
        </p>
      </div>
    </section>
  );
}

const SCRIPTURE_FACTS = [
  '114 Surahs',
  '6,236 Ayahs',
  '30 Juz',
  'Multilingual',
  'Tafsir',
  'Word-to-Word Meanings',
];

function ScriptureStrip() {
  return (
    <section className="border-y border-[#E6DED1] bg-[#FFFCF6] py-10 dark:border-night-500/20 dark:bg-night-950">
      {/* Scoped keyframes for the soft light-blue glow on each pill.
          Very subtle — opacity oscillates between 0 and ~0.35 over a
          slow 3.4s cycle. Pills are staggered so the strip "breathes"
          rather than pulsing in unison. Gated behind motion-safe. */}
      <style>{`
        @media (prefers-reduced-motion: no-preference) {
          @keyframes scripture-glow {
            0%, 100% { box-shadow: 0 0 0 0 rgba(125, 211, 252, 0); }
            50%      { box-shadow: 0 0 10px 0 rgba(125, 211, 252, 0.15); }
          }
          .scripture-pill { animation: scripture-glow 4.5s ease-in-out infinite; }
        }
      `}</style>
      <div className={SHELL}>
        <p className="mb-6 text-center text-xs font-semibold uppercase tracking-[0.2em] text-ink-500 dark:text-night-300">
          The complete Quran, faithfully rendered
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          {SCRIPTURE_FACTS.map((fact, i) => (
            <span
              key={fact}
              className="scripture-pill inline-flex items-center rounded-full border border-[#E6DED1] bg-white/75 px-4 py-2 text-sm font-medium text-[#1F2A24] shadow-[0_1px_2px_rgba(20,40,32,0.04)] backdrop-blur-sm dark:border-night-400/50 dark:bg-night-700 dark:text-night-50 dark:shadow-none"
              // Stagger each pill so the wave reads as motion rather
              // than a single uniform pulse. 320ms increments keep all
              // 6 pills within one full 3.4s cycle.
              style={{ animationDelay: `${i * 0.32}s` }}
            >
              {fact}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ================================================================== *
 * AI ask bar
 * ================================================================== */

const ASK_SUGGESTIONS = [
  'What does the Quran say about patience?',
  'Verses about gratitude',
  'The story of Prophet Yusuf',
  'Ayahs on seeking knowledge',
  'Guidance for difficult times',
];

function AskBar() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  function go(q: string) {
    const trimmed = q.trim();
    navigate(trimmed ? `/ask?q=${encodeURIComponent(trimmed)}` : '/ask');
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    go(query);
  }

  return (
    <section className="bg-[#F7F3EA] py-20 sm:py-24 dark:bg-transparent">
      <div className={cn(SHELL, 'max-w-3xl')}>
        <SectionHead
          eyebrow="Ask Alhudah"
          title="Ask a question, in your own words"
          lead="Answers are drawn only from the Quran and established tafsir — and every one links back to the ayahs it came from."
        />

        <form onSubmit={onSubmit} className="group relative mt-10">
          <div className="flex items-center gap-3 rounded-full border border-[#E6DED1] bg-white/75 px-5 py-2.5 backdrop-blur-xl transition-colors focus-within:border-accent shadow-[0_1px_3px_rgba(20,40,32,0.05)] dark:border-night-500/50 dark:bg-night-800/70 dark:focus-within:border-beam dark:shadow-none">
            <IconSparkle className="h-5 w-5 shrink-0 text-accent dark:text-beam" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Ask the Quran anything…"
              aria-label="Ask the Quran a question"
              className="h-10 w-full bg-transparent text-[#1F2A24] placeholder:text-ink-500 focus:outline-none dark:text-night-50 dark:placeholder:text-night-300"
            />
            <button
              type="submit"
              aria-label="Ask"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-beam text-beam-ink transition-transform hover:scale-105 active:scale-95"
            >
              <IconArrowRight className="h-4 w-4" />
            </button>
          </div>
        </form>

        <div className="mt-6 flex flex-wrap justify-center gap-2.5">
          {ASK_SUGGESTIONS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => go(s)}
              className="rounded-full border border-[#E6DED1] bg-white/75 px-4 py-1.5 text-xs text-ink-700 transition-colors hover:border-accent/60 hover:text-accent dark:border-night-500/40 dark:bg-night-800/60 dark:text-night-200 dark:hover:border-beam/60 dark:hover:text-beam-pale"
            >
              {s}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ================================================================== *
 * Surah grid (Stitch "Read Quran Now" — wired to live data)
 * ================================================================== */

type SurahFilter = 'all' | 'Meccan' | 'Medinan';
const SURAH_FILTERS: SurahFilter[] = ['all', 'Meccan', 'Medinan'];
const SURAH_PAGE_SIZE = 8;

function SurahGrid() {
  const { data, isLoading, isError } = useSurahList();
  const [filter, setFilter] = useState<SurahFilter>('all');
  const [page, setPage] = useState(0);

  const filtered = useMemo(() => {
    const list = data ?? [];
    if (filter === 'all') return list;
    return list.filter((s) => revelationLabel(s.revelation_type) === filter);
  }, [data, filter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / SURAH_PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const visible = filtered.slice(
    safePage * SURAH_PAGE_SIZE,
    safePage * SURAH_PAGE_SIZE + SURAH_PAGE_SIZE,
  );

  function changeFilter(next: SurahFilter) {
    setFilter(next);
    setPage(0);
  }

  return (
    // `dark` class forces dark-theme variants for everything inside,
    // regardless of root theme. SurahGrid stays visually dark in both
    // light and dark mode for intentional section rhythm.
    <section className="dark bg-night-950 py-24 sm:py-32">
      <div className={SHELL}>
        <SectionHead
          eyebrow="Read now"
          title="Start with any surah"
          lead="All 114 surahs, Meccan and Medinan, with translation and tafsir a tap away."
        />

        {/* Anchored deep-links to the most-searched surahs. Pure SEO
            value — each Link is keyword-rich anchor text pointing into
            the optimized /surah/:id pages, flowing PageRank from the
            homepage into them. */}
        <p className="mt-6 text-center text-sm text-night-200">
          Many readers start with{' '}
          <Link to="/surah/1" className="text-beam underline-offset-4 hover:underline">
            Al-Fatihah
          </Link>
          ,{' '}
          <Link to="/surah/18" className="text-beam underline-offset-4 hover:underline">
            Al-Kahf
          </Link>
          ,{' '}
          <Link to="/surah/36" className="text-beam underline-offset-4 hover:underline">
            Ya-Sin
          </Link>
          ,{' '}
          <Link to="/surah/55" className="text-beam underline-offset-4 hover:underline">
            Ar-Rahman
          </Link>
          , or{' '}
          <Link to="/surah/67" className="text-beam underline-offset-4 hover:underline">
            Al-Mulk
          </Link>
          .
        </p>

        {/* Revelation filter */}
        <div className="mt-8 flex justify-center gap-2.5">
          {SURAH_FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => changeFilter(f)}
              className={cn(
                'rounded-full px-5 py-2 text-xs font-semibold uppercase tracking-wide transition-all',
                filter === f
                  ? 'bg-beam text-beam-ink'
                  : 'border border-night-500/40 bg-night-800 text-night-200 hover:border-beam/50 hover:text-beam-pale',
              )}
            >
              {f === 'all' ? 'All' : f}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {isLoading
            ? Array.from({ length: SURAH_PAGE_SIZE }).map((_, i) => (
                <div
                  key={i}
                  className={cn(PANEL, 'h-44 animate-pulse bg-night-700/40')}
                />
              ))
            : visible.map((s) => (
                <Link
                  key={s.id}
                  to={`/surah/${s.id}`}
                  className={cn(
                    PANEL,
                    'group flex flex-col p-5 transition-all duration-200',
                    'hover:-translate-y-0.5 hover:border-beam/60 hover:bg-night-700',
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-display text-sm font-bold text-beam">
                      {String(s.id).padStart(3, '0')}
                    </span>
                    <span className="text-[10px] uppercase tracking-wide text-night-300">
                      {revelationLabel(s.revelation_type)}
                    </span>
                  </div>
                  <p
                    dir="rtl"
                    className="mt-4 font-arabic text-2xl leading-tight text-night-50"
                  >
                    {s.name_ar}
                  </p>
                  <h3 className="mt-3 font-display text-base font-bold text-night-50">
                    {s.name_en}
                  </h3>
                  <p className="mt-auto pt-2 text-xs text-night-300">
                    {s.name_translation ? `${s.name_translation} · ` : ''}
                    {s.ayah_count} ayahs
                  </p>
                </Link>
              ))}
        </div>

        {isError && (
          <p className="mt-8 text-center text-sm text-night-300">
            The surah list couldn&apos;t load just now —{' '}
            <Link to="/surah" className="text-beam hover:underline">
              open the full index
            </Link>
            .
          </p>
        )}

        {/* Pager */}
        {!isLoading && !isError && totalPages > 1 && (
          <div className="mt-12 flex items-center justify-center gap-4">
            <PagerButton
              direction="prev"
              disabled={safePage === 0}
              onClick={() => setPage(safePage - 1)}
            />
            <span className="text-sm tabular-nums text-night-200">
              {safePage + 1} / {totalPages}
            </span>
            <PagerButton
              direction="next"
              disabled={safePage >= totalPages - 1}
              onClick={() => setPage(safePage + 1)}
            />
          </div>
        )}
      </div>
    </section>
  );
}

function PagerButton({
  direction,
  disabled,
  onClick,
}: {
  direction: 'prev' | 'next';
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={direction === 'prev' ? 'Previous surahs' : 'Next surahs'}
      className={cn(
        'flex h-11 w-11 items-center justify-center rounded-full border transition-all',
        disabled
          ? 'cursor-not-allowed border-night-500/30 text-night-300 opacity-40'
          : 'border-beam/40 text-beam hover:bg-beam hover:text-beam-ink',
      )}
    >
      <IconChevronRight
        className={cn('h-5 w-5', direction === 'prev' && 'rotate-180')}
      />
    </button>
  );
}

/* ================================================================== *
 * Study tools (Stitch "Qari Services" 4-card grid)
 * ================================================================== */

const STUDY_TOOLS: {
  to: string;
  Icon: typeof IconFocus;
  title: string;
  body: string;
}[] = [
  {
    to: '/reader',
    Icon: IconFocus,
    title: 'Focused Reader',
    body: 'One ayah at a time, with recitation audio and keyboard navigation — for slow, deliberate reading.',
  },
  {
    to: '/memorize',
    Icon: IconRepeat,
    title: 'Memorize',
    body: 'Repeat, hide, and recall. A quiet workflow for committing ayahs to memory, paced entirely by you.',
  },
  {
    to: '/search',
    Icon: IconSearch,
    title: 'Search',
    body: 'Find an ayah by meaning, not just exact words — semantic and keyword search, in English and Urdu.',
  },
  {
    to: '/ask',
    Icon: IconSparkle,
    title: 'Ask Alhudah',
    body: 'Put a question to an AI grounded only in the Quran and tafsir, with every answer fully cited.',
  },
];

function StudyTools() {
  return (
    <section className="bg-[#FFFCF6] py-24 sm:py-32 dark:bg-transparent">
      <div className={SHELL}>
        <SectionHead
          eyebrow="Study tools"
          title="Four focused ways to study"
          lead="Each tool is a calm space made for a single task — nothing competing for your attention."
        />

        <div className="mx-auto mt-12 grid max-w-4xl grid-cols-1 gap-5 sm:grid-cols-2">
          {STUDY_TOOLS.map(({ to, Icon, title, body }) => (
            <Link
              key={to}
              to={to}
              className={cn(
                // Card surface: warm white in light, glass-dark in dark.
                'rounded-2xl border backdrop-blur-sm transition-all duration-200',
                'border-[#E6DED1] bg-white/80 shadow-[0_1px_3px_rgba(20,40,32,0.05)]',
                'dark:border-night-500/40 dark:bg-night-700/60 dark:shadow-none',
                'group flex gap-5 p-7',
                'hover:-translate-y-0.5 hover:border-accent/60 hover:shadow-[0_4px_12px_rgba(20,40,32,0.08)]',
                'dark:hover:border-beam/60 dark:hover:bg-night-700 dark:hover:shadow-none',
              )}
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-accent/30 bg-accent/10 text-accent dark:border-beam/30 dark:bg-beam/10 dark:text-beam">
                <Icon className="h-6 w-6" />
              </span>
              <div>
                <h3 className="font-display text-lg font-bold text-[#183B32] dark:text-night-50">
                  {title}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-ink-700 dark:text-night-200">
                  {body}
                </p>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-12 text-center">
          <ButtonLink to="/login">Create a free account</ButtonLink>
          <p className="mt-5 text-xs uppercase tracking-[0.18em] text-ink-500 dark:text-night-300">
            Free to read · An account just saves your progress
          </p>
        </div>
      </div>
    </section>
  );
}

/* ================================================================== *
 * Occasion-Based Surahs — pixel-matched twin of <StudyTools />.
 *
 * Same card pattern (icon plate left, title + body right, 2-col grid)
 * so the two sections feel like one continuous study area. Content
 * shifts from "tools" to "when to read what" — six well-known surahs
 * paired to the moments they're traditionally recited in.
 *
 * Stays in the warm-ivory light surface in light mode and the dark
 * glass surface in dark mode, mirroring StudyTools exactly.
 * ================================================================== */

const OCCASION_SURAHS: {
  to: string;
  Icon: typeof IconFocus;
  title: string;
  body: string;
}[] = [
  {
    to: '/surah/18',
    Icon: IconBookOpen,
    title: 'Friday Reflection',
    body: 'Surah Al-Kahf (18) — read each Friday for guidance and a weekly light through the week ahead.',
  },
  {
    to: '/surah/67',
    Icon: IconRepeat,
    title: 'Before Sleep',
    body: 'Surah Al-Mulk (67) — thirty short ayahs traditionally recited each night before sleep, for peace and protection.',
  },
  {
    to: '/surah/94',
    Icon: IconLightbulb,
    title: 'Hardship & Comfort',
    body: 'Surah Ash-Sharh (94) — a quiet reassurance for difficult moments and the ease that always follows.',
  },
  {
    to: '/surah/97',
    Icon: IconSparkle,
    title: 'Ramadan Nights',
    body: 'Surah Al-Qadr (97) — the chapter of the Night of Decree, better than a thousand months.',
  },
  {
    to: '/surah/36',
    Icon: IconAward,
    title: 'Morning Light',
    body: "Surah Ya-Sin (36) — called the heart of the Quran, a steady opening to the day's intention.",
  },
  {
    to: '/surah/106',
    Icon: IconArrowRight,
    title: 'Travel & Journey',
    body: 'Surah Quraysh (106) — a prayer for safety, steady provision, and a calm road ahead.',
  },
];

function OccasionSurahs() {
  return (
    <section className="bg-[#FFFCF6] py-24 sm:py-32 dark:bg-transparent">
      <div className={SHELL}>
        <SectionHead
          eyebrow="Recite & Reflect"
          title="Surahs for every occasion"
          lead="Quiet companions for the moments that matter — chosen for clarity, calm, and a sense of presence."
        />

        <div className="mx-auto mt-12 grid max-w-4xl grid-cols-1 gap-5 sm:grid-cols-2">
          {OCCASION_SURAHS.map(({ to, Icon, title, body }) => (
            <Link
              key={to}
              to={to}
              className={cn(
                // Card surface: warm white in light, glass-dark in dark.
                // Kept verbatim from <StudyTools /> so the two sections
                // pixel-match across both themes.
                'rounded-2xl border backdrop-blur-sm transition-all duration-200',
                'border-[#E6DED1] bg-white/80 shadow-[0_1px_3px_rgba(20,40,32,0.05)]',
                'dark:border-night-500/40 dark:bg-night-700/60 dark:shadow-none',
                'group flex gap-5 p-7',
                'hover:-translate-y-0.5 hover:border-accent/60 hover:shadow-[0_4px_12px_rgba(20,40,32,0.08)]',
                'dark:hover:border-beam/60 dark:hover:bg-night-700 dark:hover:shadow-none',
              )}
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-accent/30 bg-accent/10 text-accent dark:border-beam/30 dark:bg-beam/10 dark:text-beam">
                <Icon className="h-6 w-6" />
              </span>
              <div>
                <h3 className="font-display text-lg font-bold text-[#183B32] dark:text-night-50">
                  {title}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-ink-700 dark:text-night-200">
                  {body}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}



/* ================================================================== *
 * Closing CTA
 * ================================================================== */

function HomeCta() {
  return (
    <section className="relative overflow-hidden bg-[#F7F3EA] py-24 sm:py-32 dark:bg-transparent">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-1/2 hidden h-[28rem] w-[28rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-beam/6 blur-[120px] sm:block dark:bg-beam/10"
      />
      <div className={cn(SHELL, 'relative max-w-3xl text-center')}>
        <h2 className="font-display text-3xl font-extrabold tracking-tight text-[#183B32] sm:text-[2.75rem] sm:leading-tight dark:text-night-50">
          Start reading <span className="text-beam">today</span>
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-lg leading-relaxed text-ink-700 dark:text-night-200">
          Open the Quran and read a few ayahs now — translation, tafsir, and a
          quiet space made for it. No account needed to begin.
        </p>
        <div className="mt-9 flex flex-wrap justify-center gap-3">
          <ButtonLink to="/surah">
            Open the Quran
            <IconArrowRight className="h-4 w-4" />
          </ButtonLink>
          <ButtonLink to="/ask" variant="outline">
            Ask a question
          </ButtonLink>
        </div>
      </div>
    </section>
  );
}

/* ================================================================== *
 * FAQ
 * ================================================================== */

const FAQ_ITEMS: { q: string; a: string }[] = [
  {
    q: 'Is Alhudah free to use?',
    a: 'Yes. Reading the Quran with English and Urdu translation and tafsir is free, and no account is needed to begin. Signing in only saves your bookmarks and reading progress.',
  },
  {
    q: 'Where does the Arabic text come from?',
    a: 'Every letter is served verbatim from the source database in standard Uthmani script. Nothing is paraphrased, modernised, or rewritten.',
  },
  {
    q: 'How does the AI answer questions?',
    a: 'Ask answers only from the Quran and established tafsir, and every response links back to the exact ayahs it draws from — so you can always verify it yourself.',
  },
  {
    q: 'Can I use Alhudah on my phone?',
    a: 'Yes. It is fully responsive and runs in any modern mobile browser, with the same reading, search, and memorization tools as on desktop.',
  },
];

function HomeFaq() {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section className="border-t border-[#E6DED1] bg-[#FFFCF6] py-24 sm:py-32 dark:border-night-500/20 dark:bg-transparent">
      {/* FAQPage JSON-LD — mirrors FAQ_ITEMS above so the existing
          accordion is rich-result eligible. Google may now show this
          as "people also ask" cards directly in search results. */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: FAQ_ITEMS.map((item) => ({
              '@type': 'Question',
              name: item.q,
              acceptedAnswer: {
                '@type': 'Answer',
                text: item.a,
              },
            })),
          }),
        }}
      />

      <div className={cn(SHELL, 'max-w-3xl')}>
        <SectionHead
          eyebrow="FAQ"
          title="Questions, answered"
          lead="A few things worth knowing before you start."
        />

        <div className="mt-12 divide-y divide-[#E6DED1] border-y border-[#E6DED1] dark:divide-night-500/25 dark:border-night-500/25">
          {FAQ_ITEMS.map((item, i) => {
            const isOpen = open === i;
            return (
              <div key={item.q}>
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  className="flex w-full items-center justify-between gap-4 py-5 text-left"
                >
                  <span className="font-display text-lg font-semibold text-[#1F2A24] dark:text-night-50">
                    {item.q}
                  </span>
                  <IconPlus
                    className={cn(
                      'h-5 w-5 shrink-0 text-accent transition-transform duration-300 dark:text-beam',
                      isOpen && 'rotate-45',
                    )}
                  />
                </button>
                <div
                  className={cn(
                    'grid transition-all duration-300 ease-smooth',
                    isOpen
                      ? 'grid-rows-[1fr] opacity-100'
                      : 'grid-rows-[0fr] opacity-0',
                  )}
                >
                  <div className="overflow-hidden">
                    <p className="pb-5 leading-relaxed text-night-200">
                      {item.a}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

